import React, { PropsWithChildren, useState, useEffect } from 'react';
import {
    MappedComponentProperties,
} from '@adobe/aem-react-editable-components';
import { transformHTMLParserLinks } from '../../utils/rewriteLink/rewriteLink';
import SessionStorage from '../../utils/SessionStorage';
import { useAcquisitionContext } from '../../providers/AcquisitionProvider/AcquisitionProvider';
import { AcquisitionFlow, getAcquisitionFlow, getAppData, isPopularCardShown, showCardTooltip } from '../../utils/acquisition-utils';
import './PlasticCardDesign.css';
import { findMobileTooltipPosition, wrapTrademarkSuperscript } from '../../utils/cob-utils';
import { PathUtils } from '@adobe/aem-spa-page-model-manager';
import { Row, Col, Title, Image, ToolTip, Container } from '../../importCL';
import { InputRadio } from 'component-library/index';

interface IPlasticCardDesignProps extends MappedComponentProperties {
    showingTopBorder?: boolean;
    showingBottomBorder?: boolean;
    hidden?: boolean;
    title?: string;
    cardSelectionText?: string;
    cardImagePath?: string | [];
    fee?: string;
    tooltipMapping?: string | [];
    programPlasticCode?: number | [];
    cardPositionOverrides?: string | number | [];
    popularCardOverrides?: [];
    programCode?: string | [];
    inactiveCard?: boolean;
    hidingNoFeeGalleryToggle?: boolean;
    limitingNoFeeGalleryToggle?: boolean;
}

export const PlasticCardDesignEditConfig = {
    emptyLabel: 'PlasticCardDesign',
    isEmpty: (props: PropsWithChildren<IPlasticCardDesignProps>) => {
        return !props || props.isInEditor;
    },
};
const PlasticCardDesign = (props: PropsWithChildren<IPlasticCardDesignProps>): JSX.Element => {
    const [dynamicContent, setDynamicContent] = useState({
        cardImagePath: props.cardImagePath || '',
        tooltipMapping: props.tooltipMapping || '',
        cardPositionOverrides: props.cardPositionOverrides || '',
        popularCardOverrides: props.popularCardOverrides || [],
        programCode: props.programCode || '',
        inactiveCard: props.inactiveCard,
    });

    const [hidingNoFeeGalleryToggle, setHidingGalleryToggle] = useState(false);
    const [limitingNoFeeGalleryToggle, setLimitingNoFeeGalleryToggle] = useState(false);
    const [cardSelected, setCardSelected] = useState(false);
    const cardsObject = Object.values(dynamicContent.cardImagePath || []);
    const tooltipObject = Object.values(dynamicContent.tooltipMapping || []);
    const [preSelectedCard, setPreSelectedCard] = useState<number>();
    const [activeCard, setActiveCard] = useState('');
    const [windowWidth, setWindowWidth] = useState<number>(window.innerWidth);
    const [activeCardtitle, setActiveCardTitle] = useState('');
    const [activeCardFee, setActiveCardFee] = useState('');
    const [displayTooltip, setDisplayTooltip] = useState(false);
    const [_, setAllowOverrides] = useState(false); // eslint-disable-line
    const [__, setVal] = useState(''); // eslint-disable-line
    const { selectedCardId, setSelectedCardId, selectedCardValid, setSelectedCardValid, isApplicationInfoRecieved, hidingPlasticCardDesign, setHidingPlasticCardDesign } = useAcquisitionContext();
    const [sessionData, setSessionData] = useState(SessionStorage.getObject('cardInfo'));
    const [selectedProgramCode, setSelectedProgramCode] = useState(SessionStorage.get('selectedProgramCode'));
    const cardTitle: any = {};
    const cardFee: any = {};
    const cardPremium: any = {};
    const cardCode: any = {};
    const programPlasticCode: any = {};
    const isAuthor = window.location.port === '4502';

    const retrieveContent = () => {
        const parseContent = (data: any) => {
            const responseObj = {
                cardImagePath: data.cardArts,
                tooltipMapping: data.tooltipMapping,
                cardPositionOverrides: data.cardPositionOverrides,
                popularCardOverrides: data.popularCardOverrides,
                programCode: data.programCode,
                inactiveCard: props.inactiveCard,
            };
            setDynamicContent(responseObj);
            SessionStorage.set('plasticCardDesignData', JSON.stringify(responseObj));
        };

        const cachedData = SessionStorage.get('plasticCardDesignData');
        if (cachedData) {
            setDynamicContent(JSON.parse(cachedData));
        } else {
            const cards = JSON.parse(PathUtils.getMetaPropertyValue('cardDesignContent')!);
            if (cards) {
                parseContent(cards);
            } 
        }
    };
    
    useEffect(() => {
        const retrieveToggleFlagFromSessionStorage = (itemKey: 'hideCardGalleryToggle' | 'limitCardGalleryToggle') => {
            let toggle: boolean;

            if(window.location.href.includes('data-entry')) {
                toggle = itemKey === 'hideCardGalleryToggle' ? props.hidingNoFeeGalleryToggle : props.limitingNoFeeGalleryToggle;
            } else {
                toggle = JSON.parse(SessionStorage.tryGet(itemKey, false));
            }

            SessionStorage.set(itemKey, toggle.toString());
            itemKey === 'hideCardGalleryToggle' ? setHidingGalleryToggle(toggle) : setLimitingNoFeeGalleryToggle(toggle);
        } 

        retrieveToggleFlagFromSessionStorage('hideCardGalleryToggle');
        retrieveToggleFlagFromSessionStorage('limitCardGalleryToggle');

        retrieveContent();
        getAppData().then(appData => {
            setPreSelectedCard(appData[[AcquisitionFlow.PA, AcquisitionFlow.PHA].includes(getAcquisitionFlow()) ? 'SelectedProgramPlasticCode' : 'SelectedCardId']);
        });
    }, []);

    const cardOverrideArray = dynamicContent?.cardPositionOverrides ?
        Object.values(dynamicContent.cardPositionOverrides)
            .map(cardOverrideObject => JSON.parse(cardOverrideObject)) : [];

    const cardOverrides = cardOverrideArray.map((obj: any) => ({
        id: obj.keyValues2_cardIdCOO,
        overridePosition: obj.keyValues2_overrideCardPosition,
        programCodes: obj.keyValues2_programCodeCOO,
    }));

    const allowCardOverrides = () =>
        cardOverrides.some(cardOverride =>
            cardOverride?.programCodes?.includes(selectedProgramCode),
        );

    useEffect(() => {
        setAllowOverrides(allowCardOverrides());
    }, [cardOverrideArray, selectedProgramCode]);

    const overrideMap = new Map(
        cardOverrides.map(override => [override.id, override.overridePosition]),
    );

    const cardsDetail =
        sessionData !== null
            ? sessionData.map((card: any) => ({
                cardName: card.CardName,
                isPremium: card.IsPremium,
                premiumFee:
                    !card.IsPremium ||
                     card.PremiumFee === '0' ||card.PremiumFee === 0 
                        ? 'Free'
                        : card.PremiumFee,
                code: card.Code,
                programPlasticCode: card.ProgramPlasticCode,
            }))
            : [];

    const array = cardsObject.map(cardObject => JSON.parse(cardObject));
    const tooltipArray = tooltipObject.map(tooltipObject =>
        JSON.parse(tooltipObject),
    );
    const cards = array.map(obj => ({
        id: obj.keyValues0_cardId,
        image: obj.keyValues0_imagePath,
        altText: obj.keyValues0_altText,
        toolTipText: obj.keyValues0_tooltiptext,
        programCode: obj.keyValues2_programCode,
        inactive: obj.keyValues0_isInactive,
    }));

    const allowTooltipDisplay = () =>
        tooltipArray.some(tooltip =>
            tooltip?.keyValues1_programCode?.includes(selectedProgramCode),
        );

    useEffect(() => {
        setDisplayTooltip(allowTooltipDisplay());
    }, [tooltipArray, selectedProgramCode]);

    useEffect(() => {
        const errorListener = () => {
            if (selectedCardId > 0) {
                setCardSelected(true);
            } else {
                setSelectedCardValid(false);
            }
        };

        window.addEventListener('acquisitionErrorToggle', errorListener);
        window.addEventListener('acquisitionErrorToggle-cardDesign', errorListener);
        const handleResize = () => {
            setWindowWidth(window.innerWidth);
        };
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
            window.removeEventListener('acquisitionErrorToggle', errorListener);
            window.removeEventListener('acquisitionErrorToggle-cardDesign', errorListener);
        };
    }, [selectedCardId]);

    cardsDetail.forEach((obj: any) => {
        cardTitle[obj.code] = obj.cardName;
        cardFee[obj.code] = obj.premiumFee;
        cardPremium[obj.code] = obj.isPremium;
        cardCode[obj.code] = obj.cardCode;
        programPlasticCode[obj.code] = obj.programPlasticCode;
    });

    cardsDetail.forEach((card: any) => {
        if (overrideMap.has(card.code)) {
            const selectedOverride = cardOverrides.find(
                override => override.id === card.code,
            );
            if (
                selectedOverride &&
                Array.isArray(selectedOverride.programCodes) &&
                selectedOverride.programCodes.includes(selectedProgramCode)
            ) {
                card.programPlasticCode = Number(overrideMap.get(card.code));
            }
        }
    });

    const parsedPopularCards =  dynamicContent.popularCardOverrides ? dynamicContent.popularCardOverrides.map((obj) => JSON.parse(obj)) : [];
    
    const reorderedCards = cardsDetail
        .map((card: { programPlasticCode: any }) => {
            if (overrideMap.has(card.programPlasticCode)) {
                return {
                    ...card,
                    programPlasticCode: overrideMap.get(card.programPlasticCode),
                };
            }
            return card;

        })
        .sort(
            (
                a: { programPlasticCode: number },
                b: { programPlasticCode: number },
            ) => a.programPlasticCode - b.programPlasticCode,
        )
        .map((cardsDetail: any) => {
            const card = cards.find((card: any) => card.id === cardsDetail.code);
            if (card) {
                return {
                    ...cardsDetail,
                    ...card,
                    popularCardPath: '',
                    popularCardAltText: '',
                };
            }
            return null;
        })
        .filter((card: any) => card !== null)
        .map((card: any) => {
            const popCard = parsedPopularCards.find((obj) => obj.keyValues3_cardId === card.id && isPopularCardShown(obj, card));
            if (popCard) {
                card.popularCardPath = popCard.keyValues3_imagePath;
                card.popularCardAltText = popCard.keyValues3_altText;
            }
            return card;
        }).reduce((acc, curr, _, cards) => {
            const containsObjById = (array, code) => array.find(item => item.code === code);
            const containsObjByType = (array, isPremium) => array.find(item => item.isPremium === isPremium);

            if((!limitingNoFeeGalleryToggle) || (cards.every(item => !item.isPremium))) {
                return cards;
            }

            if(!containsObjById(acc, curr.code) && !containsObjByType(acc, curr.isPremium)) {
                acc.push(curr)
            }
            return acc;
        }, [])
        .sort(
            (
                a: { isPremium: boolean },
                b: { isPremium: boolean },
            ) => {
                if(!limitingNoFeeGalleryToggle)
                    return;
                if(limitingNoFeeGalleryToggle) {return Number(a.isPremium) - Number(b.isPremium)}
            }
        )
        useEffect(() => {
            if(!reorderedCards.length) {
                return;
            }
            const hasOnlyFreeCards = hidingNoFeeGalleryToggle && reorderedCards.every(card => !card.isPremium);
            if(hasOnlyFreeCards) {
                const firstFreeCard = reorderedCards.find(card => !card.isPremium);
                setSelectedCardId(firstFreeCard.programPlasticCode);
                setSelectedCardValid(!!firstFreeCard);
                setCardSelected(!!firstFreeCard);
                setActiveCard(firstFreeCard?.image);
                setActiveCardFee(cardFee[firstFreeCard?.code]);
                setActiveCardTitle(cardTitle[firstFreeCard?.code]);

                setHidingPlasticCardDesign(hasOnlyFreeCards);
            }
        }, [reorderedCards]);

    const currencyFormatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
    });

    useEffect(() => {
        if (sessionData && sessionData.length > 0) {
            if (reorderedCards.length && preSelectedCard) {
                const preSelectedCardObj = reorderedCards.find(
                    (card: any) => card.programPlasticCode === preSelectedCard,
                );
                setSelectedCardId(preSelectedCard);
                setSelectedCardValid(!!preSelectedCard);
                setCardSelected(!!preSelectedCard);
                setActiveCard(preSelectedCardObj?.image);
                setActiveCardFee(cardFee[preSelectedCardObj?.code]);
                setActiveCardTitle(cardTitle[preSelectedCardObj?.code]);
            } else if (reorderedCards?.length === 1 && reorderedCards[0] !== null) {
                setSelectedCardId(reorderedCards[0].programPlasticCode);
                setSelectedCardValid(!!reorderedCards[0]);
                setCardSelected(!!reorderedCards[0]);
                setActiveCard(reorderedCards[0]?.image);
                setActiveCardFee(cardFee[reorderedCards[0]?.code]);
                setActiveCardTitle(cardTitle[reorderedCards[0]?.code]);
            } else if (reorderedCards.length < 1 && cardsDetail.length < 2) {
                setSelectedCardId(1);
            }
        } else if (reorderedCards?.length === 0) {
            setSelectedCardId(1);
        }
    }, [dynamicContent, preSelectedCard, sessionData]);


    useEffect(() => {
        setSessionData(SessionStorage.getObject('cardInfo'));
        setSelectedProgramCode(SessionStorage.get('selectedProgramCode'));

        if (isApplicationInfoRecieved && !props.hidden && !preSelectedCard) {
            setSelectedCardId(0);
        }
    }, [isApplicationInfoRecieved, preSelectedCard]);

    useEffect(() => {
        if (props.hidden|| sessionData?.length <= 1) {
            setSelectedCardId(1);
        }
    }, [isApplicationInfoRecieved]);

    const handleCardClick = (card: any) => {
        setActiveCard(card.image);
        setActiveCardTitle(cardTitle[card.id]);
        setActiveCardFee(cardFee[card.id]);
        setCardSelected(true);
        setSelectedCardId(programPlasticCode[card.id]);
        setSelectedCardValid(true);
    };

    const handleKeyDown = (event: any, card: any) => {
        if (event.keyCode === 32 || event.keyCode === 39) {
            setActiveCard(card.image);
            setActiveCardTitle(cardTitle[card.id]);
            setCardSelected(true);
        }
    };

    return (
        <Container id='plastic-card-design' className='PlasticCardDesignWrapper'>
            {!hidingPlasticCardDesign &&
             <Row className={`PlasticCardDesign PlasticCardDesign__outerRow`}>
                {(isAuthor || (!props.hidden && sessionData?.length > 1)) && (
                    <>
                        {props.showingTopBorder && (
                            <hr className='PlasticCardDesign__hr' />
                        )}
                        <div className='PlasticCardDesign__container px-4'>
                            <Title tag='h3'>{transformHTMLParserLinks(wrapTrademarkSuperscript(props.title))}</Title>
                            <>
                                <div className='PlasticCardDesign__container__row'>
                                    {windowWidth < 1004  && <div className='PlasticCardDesign__container__row--activeCardImageAndTitle'>
                                        <Col className='PlasticCardDesign__container__row__col--titleContainer'>
                                            
                                            <p className='PlasticCardDesign__container__row__col--cardSelectionText'>
                                                {transformHTMLParserLinks(wrapTrademarkSuperscript(props.cardSelectionText))}:&nbsp;
                                                <span
                                                    className={`PlasticCardDesign__container__row__col--activeCardTitle${cardSelected
                                                        ? '--active'
                                                        : '--error'
                                                    }`}
                                                >
                                                    {activeCardtitle}
                                                    {cardSelected
                                                        ? activeCardFee === 'Free'
                                                            ? ' (Free)'
                                                            : ` ($${activeCardFee})`
                                                        : 'None'}
                                                </span>
                                            </p>
                                        </Col>
                                        {!cardSelected ? (
                                            <>
                                                { !(window.location.href.includes('verification')) && 
                                                    <a href='#cardGallery' className='PlasticCardDesign__container__row__col--buttonContainer'>
                                                        <Col
                                                            className={`PlasticCardDesign__container__row__col--activeImageContainer PlasticCardDesign__container__row__col--activeImageContainer${selectedCardValid
                                                                ? ''
                                                                : '--error'
                                                            }`}
                                                        >
                                                            {!cardSelected && (windowWidth > 1004 ? transformHTMLParserLinks('Select Your Card<br>from the Card Menu') : transformHTMLParserLinks('Select Your Card<br>from the Card Menu Below'))}
                                                        </Col>
                                                    </a>
                                                }
                                                { window.location.href.includes('verification') && 
                                                    <Col
                                                        className={`PlasticCardDesign__container__row__col--activeImageContainer PlasticCardDesign__container__row__col--activeImageContainer${selectedCardValid
                                                            ? ''
                                                            : '--error'
                                                        }`}
                                                    >
                                                        {!cardSelected && (windowWidth > 1004 ? transformHTMLParserLinks('Select Your Card<br>from the Card Menu') : transformHTMLParserLinks('Select Your Card<br>from the Card Menu Below'))}
                                                    </Col>
                                                }
                                            </>
                                        ) : (
                                            <>
                                                {activeCard && (
                                                    <div className={`PlasticCardDesign__container__row__col--activeImageContainer--image${cardSelected
                                                        ? '--active'
                                                        : ''
                                                    }`}>
                                                        <Image
                                                            loading='lazy'
                                                            src={activeCard}
                                                            alt={activeCardtitle}
                                                        />
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>}
                                    <Row
                                        className={`PlasticCardDesign__container__row--cardGallery${cards.length < 3
                                            ? '--centerJustify'
                                            : ''
                                        } PlasticCardDesign__container__row--cardGallery--leftGallery`}
                                        id='cardGallery'
                                    >
                                        {reorderedCards &&
                                        !props.inactiveCard &&
                                        reorderedCards.map((card, index) => (
                                            <Col
                                                key={index}
                                                onClick={() => handleCardClick(card)}
                                                onKeyDown={(event: any) => handleKeyDown(event, card)}
                                                className={`PlasticCardDesign__container__row__col PlasticCardDesign__container__row__col${activeCard === card.image
                                                    ? '--border'
                                                    : '--no-border'} PlasticCardDesign${
                                                    (reorderedCards?.length <= 6 )? '__twoColumns' : '__threeColumns'}`}
                                                xs={(reorderedCards?.length <= 6 )? 3 : 2} md={4} lg={4}>
                                                <div
                                                    className={`PlasticCardDesign__container__row--cardGallery--titleAndFee PlasticCardDesign__container__row--cardGallery--titleAndFee${activeCard === card.image ? '--active' : ''}  `}>
                                                    <p
                                                        className={`PlasticCardDesign__container__row--cardGallery--titleAndFee--title${activeCard === card.image ? '--active' : ''}  `}
                                                        id={card.id}
                                                        key={card.id}>
                                                        {cardTitle[card.id]}
                                                    </p>
                                                    <Col>
                                                        
                                                        <p className={`PlasticCardDesign__container__row--cardGallery--titleAndFee--fee${activeCard === card.image ? '--active' : ''}  `}>
                                                            {cardPremium[card.id] === true
                                                                ? cardFee[card.id] === 0 ||
                                                                        cardFee[card.id] === '0'
                                                                            || cardFee[card.id] === 'Free'
                                                                    ? '(Free)'
                                                                    : `(${currencyFormatter.format(
                                                                        Number(cardFee[card.id]),
                                                                    )})`
                                                                : '(Free)'}
                                                        </p>
                                                        {displayTooltip &&
                                                            showCardTooltip(tooltipObject, card.code, selectedProgramCode ? selectedProgramCode : '') &&
                                                            card.toolTipText ? (
                                                                <ToolTip
                                                                    mobileNotchPosition={findMobileTooltipPosition(index)}
                                                                    key={card.id}
                                                                    id={card.id}
                                                                    tabIndex={1}
                                                                    fill={activeCard === card.id ? '#076191' : ''}>
                                                                    <div> {transformHTMLParserLinks(card.toolTipText)}</div>
                                                                </ToolTip>
                                                            ) : null}
                                                    </Col>
                                                </div>
                                                <label className='PlasticCardDesign__container__row__col--label'>
                                                    <InputRadio
                                                        id={`selector-position-${card.id}`}
                                                        name='cards'
                                                        value={card.id}
                                                        setValue={setVal}
                                                    />
                                                    <Image
                                                        key={card.id}
                                                        loading='lazy'
                                                        id={card.id}
                                                        src={card.popularCardPath !== '' ? card.popularCardPath : card.image}
                                                        alt={card.popularCardAltText !== '' ? card.popularCardAltText : card.altText}
                                                        tabIndex={-1}
                                                        data-for={card.id}
                                                    />
                                                </label>
                                            </Col>
                                        ))}
                                    </Row>
                                    {windowWidth >= 1004 && <div className='PlasticCardDesign__container__row--activeCardImageAndTitle'>
                                        <Col className='PlasticCardDesign__container__row__col--titleContainer'>
                                            
                                            <p className='PlasticCardDesign__container__row__col--cardSelectionText'>
                                                {transformHTMLParserLinks(wrapTrademarkSuperscript(props.cardSelectionText))}:&nbsp;
                                                <span
                                                    className={`PlasticCardDesign__container__row__col--activeCardTitle${cardSelected
                                                        ? '--active'
                                                        : '--error'
                                                    }`}
                                                >
                                                    {activeCardtitle}
                                                    {cardSelected
                                                        ? activeCardFee === 'Free'
                                                            ? ' (Free)'
                                                            : ` ($${activeCardFee})`
                                                        : 'None'}
                                                </span>
                                            </p>
                                        </Col>
                                        {!cardSelected ? (
                                            <Col
                                                className={`PlasticCardDesign__container__row__col--activeImageContainer PlasticCardDesign__container__row__col--activeImageContainer${selectedCardValid
                                                    ? ''
                                                    : '--error'
                                                }`}
                                            >
                                                {!cardSelected &&
                                                windowWidth > 1004 &&
                                                transformHTMLParserLinks('Select Your Card<br>from the Card Menu')}
                                                {!cardSelected &&
                                                windowWidth <= 1004 &&
                                                transformHTMLParserLinks('Select Your Card<br>from the Card Menu Below')}
                                            </Col>
                                        ) : (
                                            <>
                                                {activeCard && (
                                                    <div className={`PlasticCardDesign__container__row__col--activeImageContainer--image${cardSelected
                                                        ? '--active'
                                                        : ''
                                                    }`}>
                                                        <Image
                                                            loading='lazy'
                                                            src={activeCard}
                                                            alt={activeCardtitle}
                                                        />
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>}
                                </div>
                            </>
                        </div>
                        {props.showingBottomBorder && (
                            <hr className='PlasticCardDesign__hr' />
                        )} 
                    </>
                )}
            </Row>
            }
        </Container>
    );
};

export default PlasticCardDesign;
